import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import Icon from './Icon';
import { getColumnWidthClass, getPageWindow, getSerialMenuPosition, isSerialField, orderSoftwareColumns } from './utils/softwareTable';
import './Software.css';

const Software = ({ initialFilters = {}, searchSerial = null }) => {
  const { selectedTenant, authenticatedFetch } = useAuth();
  const [software, setSoftware] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [searchFilters, setSearchFilters] = useState(initialFilters);
  const [filtersCleared, setFiltersCleared] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [serialMenu, setSerialMenu] = useState(null);
  const serialMenuRef = useRef(null);

  const fetchSoftware = useCallback(async () => {
    if (!selectedTenant) {
      setError('No tenant selected');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/software?tenant=${selectedTenant.name}`);

      const data = await response.json();

      if (response.ok) {
        setSoftware(data.software);
        setColumns(data.columns);
      } else {
        setError(data.error || 'Failed to fetch software');
      }
    } catch (error) {
      console.error('Error fetching software:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedTenant, authenticatedFetch]);

  useEffect(() => {
    if (selectedTenant) {
      fetchSoftware();
    }
  }, [selectedTenant, fetchSoftware]);

  useEffect(() => {
    if (Object.keys(initialFilters).length > 0) {
      setSearchFilters(initialFilters);
      setFiltersCleared(false);
    }
  }, [initialFilters]);

  const formatValue = (value, column) => {
    if (value === null || value === undefined) {
      return <span className="null-value">-</span>;
    }
    
    // Format based on column type
    if (column.type && column.type.includes('int')) {
      return value.toString();
    }
    
    if (column.type && column.type.includes('date')) {
      return new Date(value).toLocaleDateString();
    }
    
    if (column.type && column.type.includes('datetime')) {
      return new Date(value).toLocaleString();
    }
    
    return value.toString();
  };

  const getColumnDisplayName = (fieldName) => {
    // Convert field names to display names
    const displayNames = {
      'id': 'ID',
      'name': 'Software Name',
      'version': 'Version',
      'publisher': 'Publisher',
      'install_date': 'Install Date',
      'size': 'Size',
      'license': 'License',
      'status': 'Status',
      'category': 'Category',
      'description': 'Description',
      'serial': 'Installed on',
      'serial_number': 'Installed on',
      'asset_serial': 'Installed on',
      'device_serial': 'Installed on',
      'source': 'UPN'
    };
    
    return displayNames[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Filter out ID column and unwanted columns, then put serial last
  const filteredColumns = columns.filter(column => {
    const fieldName = column.field.toLowerCase();
    return fieldName !== 'id' && 
           fieldName !== 'installed' && 
           fieldName !== 'created' && 
           fieldName !== 'sync-date' &&
           fieldName !== 'source' &&
           fieldName !== 'upn';
  });
  const orderedColumns = orderSoftwareColumns(filteredColumns);

  useEffect(() => {
    if (!serialMenu) return undefined;

    const handlePointerDown = (event) => {
      if (serialMenuRef.current && serialMenuRef.current.contains(event.target)) {
        return;
      }
      setSerialMenu(null);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSerialMenu(null);
      }
    };

    const handleViewportChange = () => setSerialMenu(null);

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleViewportChange, true);
    window.addEventListener('resize', handleViewportChange);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleViewportChange, true);
      window.removeEventListener('resize', handleViewportChange);
    };
  }, [serialMenu]);

  // Handle search filter changes
  const handleSearchChange = (columnField, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [columnField]: value.toLowerCase()
    }));
    setCurrentPage(1);
  };

  const openSerialMenu = (event, menuKey, field, serialValue) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    const { top, right } = getSerialMenuPosition(rect, window.innerWidth);
    setSerialMenu((prev) => {
      if (prev && prev.menuKey === menuKey) {
        return null;
      }
      return {
        menuKey,
        field,
        serial: String(serialValue),
        top,
        right
      };
    });
  };

  const filterByDevice = (field, serialValue) => {
    setFiltersCleared(true);
    handleSearchChange(field, serialValue);
    setSerialMenu(null);
  };

  const showInAssets = (serialValue) => {
    setSerialMenu(null);
    window.dispatchEvent(new CustomEvent('navigateToAssets', {
      detail: { initialFilters: { serial: String(serialValue).toLowerCase() } }
    }));
  };

  // Clear all search filters
  const clearAllFilters = () => {
    setSearchFilters({});
    setFiltersCleared(true);
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  // Filter and sort software based on search filters and sort configuration
  const filteredAndSortedSoftware = [...software]
    .filter(item => {
      // If we have a searchSerial prop and filters haven't been cleared, find the field that contains it
      if (searchSerial && !filtersCleared) {
        const hasSerialMatch = Object.keys(item).some(field => {
          const itemValue = item[field];
          if (itemValue === null || itemValue === undefined) return false;
          return String(itemValue).toLowerCase().includes(searchSerial);
        });
        if (!hasSerialMatch) return false;
      }
      
      // Apply search filters
      return Object.entries(searchFilters).every(([columnField, searchValue]) => {
        if (!searchValue) return true; // No filter applied for this column
        
        const itemValue = item[columnField];
        if (itemValue === null || itemValue === undefined) return false;
        
        const itemValueLower = String(itemValue).toLowerCase();
        const searchValueLower = String(searchValue).toLowerCase();
        
        return itemValueLower.includes(searchValueLower);
      });
    })
    .sort((a, b) => {
      if (!sortConfig.key) return 0;
      
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      // Convert to string for comparison
      const aStr = String(aValue);
      const bStr = String(bValue);
      return sortConfig.direction === 'asc' 
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });

  // Pagination logic
  const totalPages = Math.ceil(filteredAndSortedSoftware.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSoftware = filteredAndSortedSoftware.slice(startIndex, endIndex);
  const { pages: visiblePages, hasMore: hasMorePages, nextWindowPage } = getPageWindow(currentPage, totalPages);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
  };

  // Handle column sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  if (loading) {
    return (
      <div className="software-container">
        <div className="software-header">
          <h2>Software - {selectedTenant?.name || 'No Tenant Selected'}</h2>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading software...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="software-container">
        <div className="software-header">
          <h2>Software - {selectedTenant?.name || 'No Tenant Selected'}</h2>
        </div>
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h3>Error Loading Software</h3>
          <p>{error}</p>
          <button onClick={fetchSoftware} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!selectedTenant) {
    return (
      <div className="software-container">
        <div className="software-header">
          <h2>Software</h2>
        </div>
        <div className="no-tenant-container">
          <div className="no-tenant-icon">🏢</div>
          <h3>No Tenant Selected</h3>
          <p>Please select a tenant from the dropdown to view software.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="software-container">
      <div className="software-header">
        <h2>Discovered software</h2>
        <div className="software-actions">
          {Object.keys(searchFilters).length > 0 && (
            <button onClick={clearAllFilters} className="clear-filters-button">
              Clear Filters
            </button>
          )}
          <span className="software-count">
            {filteredAndSortedSoftware.length} of {software.length} software item{software.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {software.length === 0 ? (
        <div className="no-software-container">
          <Icon name="software" size="large" className="no-software-icon" />
          <h3>No Software Found</h3>
          <p>No software found for tenant "{selectedTenant.name}".</p>
        </div>
      ) : (
        <div className="software-grid-container">
          <div className="software-grid">
            <table className="software-table discovered-software-table">
              <thead>
                <tr>
                  {orderedColumns.map((column) => (
                    <th 
                      key={column.field} 
                      className={`table-header sortable ${getColumnWidthClass(column.field)}`}
                      onClick={() => handleSort(column.field)}
                    >
                      <div className="header-content">
                        <span>{getColumnDisplayName(column.field)}</span>
                        {sortConfig.key === column.field && (
                          <span className="sort-indicator">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
                <tr className="search-row">
                  {orderedColumns.map((column) => (
                    <th key={`search-${column.field}`} className={`search-header ${getColumnWidthClass(column.field)}`}>
                      <input
                        type="text"
                        placeholder={`Search ${getColumnDisplayName(column.field)}...`}
                        value={searchFilters[column.field] || ''}
                        onChange={(e) => handleSearchChange(column.field, e.target.value)}
                        className="column-search-input"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedSoftware.map((item, index) => (
                  <tr key={item.id || index} className="table-row">
                    {orderedColumns.map((column) => {
                      const cellValue = item[column.field];
                      const hasValue = cellValue && cellValue !== '';
                      const columnClass = getColumnWidthClass(column.field);
                      const serialColumn = isSerialField(column.field);
                      const menuKey = `${item.id || index}-${column.field}`;
                      const isMenuOpen = serialMenu?.menuKey === menuKey;

                      if (serialColumn && hasValue) {
                        return (
                          <td
                            key={column.field}
                            className={`table-cell clickable-cell serial-cell ${columnClass} ${isMenuOpen ? 'menu-open' : ''}`}
                          >
                            <button
                              type="button"
                              className="serial-cell-trigger"
                              aria-haspopup="menu"
                              aria-expanded={isMenuOpen}
                              onMouseDown={(event) => event.stopPropagation()}
                              onClick={(event) => openSerialMenu(event, menuKey, column.field, cellValue)}
                            >
                              <span className="serial-cell-text">{formatValue(item[column.field], column)}</span>
                              <span className="serial-cell-caret" aria-hidden="true">▼</span>
                            </button>
                          </td>
                        );
                      }
                      
                      const handleCellClick = () => {
                        if (hasValue) {
                          handleSearchChange(column.field, cellValue);
                        }
                      };
                      
                      return (
                        <td 
                          key={column.field} 
                          className={`table-cell ${columnClass} ${hasValue ? 'clickable-cell' : ''}`}
                          onClick={handleCellClick}
                          style={{ 
                            cursor: hasValue ? 'pointer' : 'default' 
                          }}
                        >
                          {formatValue(item[column.field], column)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="pagination-container">
              <div className="pagination-info">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredAndSortedSoftware.length)} of {filteredAndSortedSoftware.length} entries
              </div>
              <div className="pagination-controls">
                <button 
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pagination-button"
                >
                  Previous
                </button>
                
                <div className="pagination-numbers">
                  {visiblePages.map(page => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                    >
                      {page}
                    </button>
                  ))}
                  {hasMorePages && (
                    <button
                      type="button"
                      onClick={() => handlePageChange(nextWindowPage)}
                      className="pagination-number pagination-more"
                      title={`Go to page ${nextWindowPage}`}
                      aria-label={`Show next pages, starting at page ${nextWindowPage}`}
                    >
                      +
                    </button>
                  )}
                </div>
                
                <button 
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="pagination-button"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {serialMenu && (
        <div
          ref={serialMenuRef}
          className="serial-cell-menu"
          role="menu"
          style={{ top: serialMenu.top, right: serialMenu.right }}
        >
          <button
            type="button"
            role="menuitem"
            className="serial-cell-menu-item"
            onClick={() => filterByDevice(serialMenu.field, serialMenu.serial)}
          >
            Filter by this device
          </button>
          <button
            type="button"
            role="menuitem"
            className="serial-cell-menu-item"
            onClick={() => showInAssets(serialMenu.serial)}
          >
            Show in Assets
          </button>
        </div>
      )}
    </div>
  );
};

export default Software;
